/**
 * RentalPriceOverridesEditor — inline table of date-window price
 * overrides for a rental product. Each row: start_date / end_date /
 * per_day / optional label. Lives inside the product's per-row config
 * panel on SellerRentalsTab.
 */
import React from "react";
import { Calendar, Trash2, Plus } from "lucide-react";


const blank = () => ({ start_date: "", end_date: "", per_day: "", label: "" });


export default function RentalPriceOverridesEditor({ overrides = [], onChange, basePerDay }) {
  const rows = overrides.length > 0 ? overrides : [blank()];

  const setRow = (i, k, v) => {
    const next = rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r);
    onChange(next.filter(r => r.start_date && r.end_date && r.per_day));
  };
  const add = () => onChange([...rows, blank()]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i)
    .filter(r => r.start_date && r.end_date && r.per_day));

  return (
    <div className="mt-3 border-t border-[#3D2845] pt-3"
      data-testid="rental-overrides-editor">
      <div className="flex items-center gap-2 mb-2">
        <Calendar size={11} className="text-[#00F0FF]"/>
        <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA]">
          Seasonal pricing
        </div>
        <span className="text-[10px] text-[#A1A1AA]">— charge a different daily rate during peak / wedding / festive windows.</span>
      </div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={`ov-${i}-${r.start_date}`}
            data-testid={`override-row-${i}`}
            className="grid grid-cols-[1fr_1fr_90px_1fr_32px] gap-2 items-center">
            <input type="date" value={r.start_date}
              onChange={e => setRow(i, "start_date", e.target.value)}
              data-testid={`override-start-${i}`}
              className="input-dark text-[11px]"/>
            <input type="date" value={r.end_date} min={r.start_date}
              onChange={e => setRow(i, "end_date", e.target.value)}
              data-testid={`override-end-${i}`}
              className="input-dark text-[11px]"/>
            <input type="number" min="1" step="1" placeholder="$/day"
              value={r.per_day}
              onChange={e => setRow(i, "per_day", e.target.value)}
              data-testid={`override-perday-${i}`}
              className="input-dark text-[11px]"/>
            <input placeholder="Label e.g. Diwali" value={r.label || ""}
              onChange={e => setRow(i, "label", e.target.value)}
              className="input-dark text-[11px]"/>
            <button type="button" onClick={() => remove(i)}
              data-testid={`override-remove-${i}`}
              className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#FF007F] hover:text-white flex items-center justify-center">
              <Trash2 size={11}/>
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add}
        data-testid="override-add-row"
        className="mt-2 btn-secondary text-[10px] px-3 py-1.5 inline-flex items-center gap-1">
        <Plus size={10}/>Add window
      </button>
      {basePerDay && (
        <div className="mt-2 text-[9px] text-[#A1A1AA]">
          ✓ Base rate is <span className="text-[#D4FF00] font-bold">${basePerDay}/day</span>. Days outside every window use the base rate.
        </div>
      )}
    </div>
  );
}
